
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Play, Calendar, Info } from "lucide-react";

const apiBase = process.env.REACT_APP_API;

const DrivePlayer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get("id");

  const [videoDetail, setVideoDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideoDetail = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication required. Redirecting to login...");
        navigate("/login");
        return;
      }

      try {
        const response = await fetch(`${apiBase}/lessons/video/${id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if(!response.ok) throw response
        const data = await response.json();
        if (data.error) throw data.error;

        setVideoDetail(data);
      } catch (error) {
        const data = await error.json();
        toast.error(data?.message);
        console.error(error.message);
        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchVideoDetail();
    } else {
      toast.error("Invalid video ID provided.");
      setLoading(false);
    }
  }, [id, navigate]);

  const extractFileId = (link) => {
    const match = link?.match(/[-\w]{25,}/);
    return match ? match[0] : null;
  };

  // Loading animation component
  const LoadingSpinner = () => (
    <div className="flex flex-col items-center justify-center min-h-screen">
      <div className="relative w-24 h-24">
        <div className="absolute top-0 left-0 w-full h-full border-4 border-gray-200 rounded-full"></div>
        <div className="absolute top-0 left-0 w-full h-full border-4 border-transparent border-t-blue-600 rounded-full animate-spin"></div>
      </div>
      <p className="mt-4 text-lg font-medium text-gray-700">Loading video...</p>
    </div>
  );

  // Error display component
  const ErrorDisplay = ({ message }) => (
    <div className="flex flex-col items-center justify-center min-h-screen px-4">
      <div className="p-6 bg-red-50 rounded-lg border border-red-100 shadow-sm">
        <div className="flex items-center mb-4">
          <Info className="w-6 h-6 text-red-500 mr-2" />
          <h3 className="text-lg font-semibold text-red-700">Error</h3>
        </div>
        <p className="text-red-600">{message}</p>
        <button 
          onClick={() => navigate('/')}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Go Back Home
        </button>
      </div>
    </div>
  );

  // Handle loading state
  if (loading) {
    return <LoadingSpinner />;
  }

  // Handle error states
  if (!videoDetail) {
    return <ErrorDisplay message="Error loading video details. Please try again later." />;
  }

  const fileId = extractFileId(videoDetail.URL);
  if (!fileId) {
    return <ErrorDisplay message="Invalid video link provided." />;
  }

  const embedUrl = `https://drive.google.com/file/d/${fileId}/preview`;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 mt-16">
      <div className="bg-white rounded-lg overflow-hidden">
        {/* Video player */}
        <div className="relative pb-9/16 w-full" style={{ paddingBottom: "56.25%" }}>
          <iframe
            src={embedUrl}
            title={videoDetail.title || "Google Drive Video Player"}
            className="absolute top-0 left-0 w-full h-full border-none"
            allow="autoplay"
            allowFullScreen
            sandbox="allow-same-origin allow-scripts"
          ></iframe>
        </div>
        
        {/* Video details */}
        <div className="">
          <div className="title my-4 rounded-lg bg-myBlue p-4 md:p-8 text-base md:text-lg font-semibold text-white">
            <div>
              <h2 className="text-2xl font-bold">{videoDetail.title}</h2>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DrivePlayer;